/**
 * About Me Component
 * Displays and manages user's bio, interests, and personality traits
 */

import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';

/**
 * About Me Component
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Bio data
 * @param {boolean} props.isEditing - Whether the component is in edit mode
 * @param {Function} props.onChange - Function to handle data changes
 * @returns {JSX.Element} About me component
 */
const AboutMe = ({ data, isEditing, onChange }) => {
  const [formData, setFormData] = useState({
    shortBio: '',
    longBio: '',
    personalities: [],
    interests: [],
    relationshipGoal: ''
  });
  
  // Character limits
  const shortBioLimit = 150;
  const longBioLimit = 500;
  
  // Personality traits options
  const personalityOptions = [
    'Adventurous', 'Ambitious', 'Analytical', 'Artistic', 'Athletic',
    'Calm', 'Caring', 'Cheerful', 'Committed', 'Compassionate',
    'Creative', 'Curious', 'Detail-oriented', 'Determined', 'Diligent',
    'Empathetic', 'Energetic', 'Enthusiastic', 'Flexible', 'Friendly',
    'Funny', 'Generous', 'Goal-oriented', 'Hardworking', 'Helpful',
    'Honest', 'Humorous', 'Imaginative', 'Independent', 'Innovative',
    'Intelligent', 'Kind', 'Logical', 'Loyal', 'Methodical',
    'Open-minded', 'Optimistic', 'Organized', 'Outgoing', 'Patient',
    'Persistent', 'Practical', 'Problem-solver', 'Punctual', 'Rational',
    'Reliable', 'Reserved', 'Resilient', 'Responsible', 'Sensitive',
    'Sincere', 'Social', 'Spontaneous', 'Thoughtful', 'Understanding'
  ].sort();
  
  // Interest options
  const interestOptions = [
    'Art', 'Books', 'Camping', 'Coding', 'Community Service',
    'Cooking', 'Dance', 'Design', 'Entrepreneurship', 'Fashion',
    'Fitness', 'Gaming', 'Hiking', 'History', 'Languages',
    'Mathematics', 'Movies', 'Music', 'Photography', 'Politics',
    'Reading', 'Science', 'Singing', 'Sports', 'Swimming',
    'Technology', 'Theater', 'Travel', 'Volunteering', 'Writing',
    'Yoga'
  ].sort();
  
  // Relationship goal options
  const relationshipGoalOptions = [
    { value: 'friends', label: 'Looking for Friends' },
    { value: 'study-partners', label: 'Looking for Study Partners' },
    { value: 'dating', label: 'Open to Dating' },
    { value: 'networking', label: 'Professional Networking' },
    { value: 'not-specified', label: 'Prefer Not to Say' }
  ];
  
  // Initialize form data
  useEffect(() => {
    if (data) {
      setFormData({
        shortBio: data.shortBio || '',
        longBio: data.longBio || '',
        personalities: data.personalities || [],
        interests: data.interests || [],
        relationshipGoal: data.relationshipGoal || ''
      });
    }
  }, [data]);
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Apply character limits
    if (name === 'shortBio' && value.length > shortBioLimit) return;
    if (name === 'longBio' && value.length > longBioLimit) return;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    onChange({ [name]: value });
  };
  
  // Handle personality selection
  const handlePersonalityToggle = (personality) => {
    let updatedPersonalities;
    
    if (formData.personalities.includes(personality)) {
      // Remove personality if already selected
      updatedPersonalities = formData.personalities.filter(p => p !== personality);
    } else {
      // Add personality if not selected (max 5)
      if (formData.personalities.length >= 5) return;
      updatedPersonalities = [...formData.personalities, personality];
    }
    
    setFormData((prev) => ({
      ...prev,
      personalities: updatedPersonalities
    }));
    onChange({ personalities: updatedPersonalities });
  };
  
  // Handle interest selection
  const handleInterestToggle = (interest) => {
    let updatedInterests;
    
    if (formData.interests.includes(interest)) {
      // Remove interest if already selected
      updatedInterests = formData.interests.filter(i => i !== interest);
    } else {
      // Add interest if not selected (max 10)
      if (formData.interests.length >= 10) return;
      updatedInterests = [...formData.interests, interest];
    }
    
    setFormData((prev) => ({
      ...prev,
      interests: updatedInterests
    }));
    onChange({ interests: updatedInterests });
  };

  // Render edit mode
  if (isEditing) {
    return (
      <div className="space-y-8">
        {/* Short Bio */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <label htmlFor="shortBio" className="block text-sm font-medium text-gray-700">
              Short Bio
            </label>
            <span className="text-xs text-gray-500">
              {formData.shortBio.length}/{shortBioLimit}
            </span>
          </div>
          <textarea
            name="shortBio"
            id="shortBio"
            rows={2}
            value={formData.shortBio}
            onChange={handleInputChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
            placeholder="Write a brief description of yourself (150 characters max)"
          />
          <p className="mt-1 text-xs text-gray-500">
            This will appear on your profile preview and in search results
          </p>
        </div>
        
        {/* Long Bio */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <label htmlFor="longBio" className="block text-sm font-medium text-gray-700">
              About Me
            </label>
            <span className="text-xs text-gray-500">
              {formData.longBio.length}/{longBioLimit}
            </span>
          </div>
          <textarea
            name="longBio"
            id="longBio"
            rows={5}
            value={formData.longBio}
            onChange={handleInputChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
            placeholder="Tell others more about yourself, your interests, and what you're looking for (500 characters max)"
          />
        </div>
        
        {/* Personality Traits */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Personality Traits
            </label>
            <span className="text-xs text-gray-500">
              {formData.personalities.length}/5
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {personalityOptions.map((personality) => (
              <button
                key={personality}
                type="button"
                onClick={() => handlePersonalityToggle(personality)}
                disabled={formData.personalities.length >= 5 && !formData.personalities.includes(personality)}
                className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                  formData.personalities.includes(personality)
                    ? 'bg-green-100 text-green-800 border border-green-300'
                    : formData.personalities.length >= 5
                      ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                }`}
              >
                {personality}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Select up to 5 traits that describe your personality
          </p>
        </div>
        
        {/* Interests */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Interests
            </label>
            <span className="text-xs text-gray-500">
              {formData.interests.length}/10
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {interestOptions.map((interest) => (
              <button
                key={interest}
                type="button"
                onClick={() => handleInterestToggle(interest)}
                disabled={formData.interests.length >= 10 && !formData.interests.includes(interest)}
                className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                  formData.interests.includes(interest)
                    ? 'bg-green-100 text-green-800 border border-green-300'
                    : formData.interests.length >= 10
                      ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                }`}
              >
                {interest}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Select up to 10 interests to help connect with like-minded students
          </p>
        </div>
        
        {/* Relationship Goal */}
        <div>
          <label htmlFor="relationshipGoal" className="block text-sm font-medium text-gray-700 mb-1">
            What are you looking for on VarsityLink?
          </label>
          <select
            name="relationshipGoal"
            id="relationshipGoal"
            value={formData.relationshipGoal}
            onChange={handleInputChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
          >
            <option value="">Select an option</option>
            {relationshipGoalOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  // Render view mode
  return (
    <div className="space-y-6">
      {/* Short Bio */}
      <div>
        <h3 className="text-sm font-medium text-gray-500">Short Bio</h3>
        <p className="mt-1 text-base text-gray-900">
          {formData.shortBio || 'No short bio provided'}
        </p>
      </div>
      
      {/* Long Bio */}
      <div>
        <h3 className="text-sm font-medium text-gray-500">About Me</h3>
        <p className="mt-1 text-base text-gray-900 whitespace-pre-line">
          {formData.longBio || 'No details provided'}
        </p>
      </div>
      
      {/* Personality Traits */}
      <div>
        <h3 className="text-sm font-medium text-gray-500">Personality Traits</h3>
        {formData.personalities && formData.personalities.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {formData.personalities.map((personality) => (
              <span
                key={personality}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
              >
                {personality}
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-base text-gray-900">No personality traits selected</p>
        )}
      </div>
      
      {/* Interests */}
      <div>
        <h3 className="text-sm font-medium text-gray-500">Interests</h3>
        {formData.interests && formData.interests.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {formData.interests.map((interest) => (
              <span
                key={interest}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {interest}
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-base text-gray-900">No interests selected</p>
        )}
      </div>
      
      {/* Relationship Goal */}
      <div>
        <h3 className="text-sm font-medium text-gray-500">Looking For</h3>
        <p className="mt-1 text-base text-gray-900">
          {relationshipGoalOptions.find(option => option.value === formData.relationshipGoal)?.label || 'Not specified'}
        </p>
      </div>
    </div>
  );
};

export default AboutMe;